import { EChartsOption } from "echarts";

import { FeatureImportanceSummary } from "../types";
import { EChartsTransformer } from "./transformer";
import { formatAxisLabel, stripCodeSuffix } from "./utils/labelFormatter";

function roundDownToNice(value: number) {
  if (value === 0) return 0;
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const magnitude = Math.pow(10, exponent);
  return Math.floor(value / magnitude) * magnitude;
}

function roundUpToNice(value: number) {
  if (value === 0) return 0;
  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const magnitude = Math.pow(10, exponent);
  return Math.ceil(value / magnitude) * magnitude;
}

const parseTextInteractionValue = (value: unknown) => {
  if (typeof value !== "string") {
    return {
      numeric: typeof value === "number" && Number.isFinite(value) ? value : undefined,
      label: undefined
    };
  }

  const numericMatch = value.match(/\(\s*(-?\d+)\s*\)/);
  const numeric = numericMatch ? Number(numericMatch[1]) : undefined;
  const label = value
    .replace(/\(\s*-?\d+\s*\)\s*/g, "")
    .replace(/^"|"$/g, "")
    .trim();

  return {
    numeric: Number.isFinite(numeric) ? numeric : undefined,
    label: label || value
  };
};
export const shapDependenceTransformer: EChartsTransformer<FeatureImportanceSummary> = {
  transform: (summary, _, context, __, target) => {
    if (!summary?.shap_dependence) {
      return {};
    }
    const keys = Object.keys(summary.shap_dependence).filter((key) =>
      key.startsWith(context.selectedFeature)
    );

    if (keys.length == 0) {
      return {
        title: {
          text: "No feature found",
          left: "center"
        }
      };
    }
    const isFullscreen = context?.isFullscreen ?? false;
    const foundFeature = keys[0];
    const dependenceData = summary.shap_dependence[foundFeature];
    const scatterData = dependenceData.scatter_data;
    const interactionFeature =
      context?.interactionFeature !== undefined
        ? context.interactionFeature
        : dependenceData.interaction_feature;

    // Clean feature names for display (remove _CODE suffix)
    const displayFeatureName = stripCodeSuffix(foundFeature);
    const displayInteractionFeature = interactionFeature
      ? stripCodeSuffix(interactionFeature)
      : null;

    const targetUnitLabel = target?.Unit ? ` (${target.Unit})` : "";

    const xValues = scatterData
      .map((d) => parseTextInteractionValue(d.x).numeric)
      .filter((v): v is number => typeof v === "number");
    const xMinRaw = Math.min(...xValues);
    const xMaxRaw = Math.max(...xValues);
    const xPadding = (xMaxRaw - xMinRaw) * 0.05;
    let xMin = roundDownToNice(xMinRaw - xPadding);
    // Only allow negative axis if data contains negative values
    if (xMinRaw >= 0 && xMin < 0) {
      xMin = 0;
    }
    const xMax = roundUpToNice(xMaxRaw + xPadding);
    const processedScatterData = scatterData.map((d) => {
      const { numeric: xNumeric, label: xLabel } = parseTextInteractionValue(d.x);
      const interactionValueRaw =
        interactionFeature && d.interaction_values
          ? d.interaction_values[interactionFeature]
          : undefined;
      const { numeric, label } = parseTextInteractionValue(interactionValueRaw);

      return {
        value: [xNumeric ?? d.x, d.y],
        entity: d.entity,
        interaction_value: numeric,
        interaction_value_label: label,
        x: xNumeric ?? d.x,
        x_label: xLabel,
        y: d.y
      };
    });

    const xLabelMap = new Map<number, string>();
    processedScatterData.forEach((point) => {
      if (typeof point.x === "number" && point.x_label) {
        xLabelMap.set(point.x, point.x_label);
      }
    });

    // Extract sorted category labels for category axis
    const categoryLabels =
      xLabelMap.size > 0
        ? Array.from(xLabelMap.entries())
            .sort((a, b) => a[0] - b[0])
            .map((entry) => entry[1])
        : [];

    const option: EChartsOption = {
      animation: false,
      tooltip: {
        formatter: function (params) {
          if (!params || !params.data) {
            return;
          }
          const dataPoint = params.data;
          let tooltip = `<strong>${dataPoint.entity}</strong> <br/>`;
          if (dataPoint.x_label) {
            tooltip += `${displayFeatureName}: ${dataPoint.x_label}<br/>`;
          } else {
            tooltip += `${displayFeatureName}: ${dataPoint.x.toFixed(2)}<br/>`;
          }
          tooltip += `SHAP value${targetUnitLabel}: ${dataPoint.y.toFixed(4)}<br/>`;
          if (displayInteractionFeature) {
            if (dataPoint.interaction_value_label) {
              tooltip += `${displayInteractionFeature}: ${dataPoint.interaction_value_label}<br/>`;
            } else if (typeof dataPoint.interaction_value === "number") {
              tooltip += `${displayInteractionFeature}: ${dataPoint.interaction_value.toFixed(
                4
              )}<br/>`;
            }
          }
          return tooltip;
        }
      },
      grid: {
        left: 60,
        right: 25,
        top: 10,
        bottom: xLabelMap.size > 0 ? 80 : 45
      },
      xAxis: {
        type: xLabelMap.size > 0 ? "category" : "value",
        name: (() => {
          return String(displayFeatureName);
        })(),
        nameLocation: "middle",
        nameGap: xLabelMap.size > 0 ? 65 : 30,
        ...(xLabelMap.size > 0 && { data: categoryLabels }),
        axisLabel: {
          formatter: function (value) {
            // If we have categorical data (string labels), format the category labels
            if (xLabelMap.size > 0) {
              return formatAxisLabel({
                value: String(value),
                dataLength: xLabelMap.size,
                isFullscreen,
                maxCharsPerLine: 12,
                maxFeaturesToAllowTextWrap: 12
              });
            }
            return String(value);
          },
          fontSize: xLabelMap.size > 0 ? 10 : 12,
          rotate: xLabelMap.size > 0 ? 33 : 0,
          width: xLabelMap.size > 0 ? 90 : 50,
          overflow: "truncate",
          lineHeight: xLabelMap.size > 0 ? 9.5 : 12,
          margin: xLabelMap.size > 0 && isFullscreen ? 9 : 8,
          hideOverlap: true,
          interval: xLabelMap.size > 0 ? 0 : undefined
        },
        ...(xLabelMap.size === 0 && {
          min: xMin,
          max: xMax
        }),
        minInterval: xLabelMap.size > 0 ? 1 : undefined
      },
      yAxis: {
        type: "value",
        name: `SHAP Value${targetUnitLabel}`,
        nameLocation: "middle",
        nameGap: 45
      },
      dataZoom: [
        { type: "inside", xAxisIndex: 0, filterMode: "none" },
        { type: "inside", yAxisIndex: 0, filterMode: "none" }
      ],
      series: [
        {
          name: "SHAP Values",
          type: "scatter",
          data: processedScatterData,
          symbolSize: 6,
          itemStyle: interactionFeature
            ? {
                color: function (params: unknown) {
                  const value = (params as { data: { interaction_value: number } })?.data
                    ?.interaction_value;
                  if (value === undefined) {
                    return "rgba(70, 130, 180, 0.7)";
                  }
                  const interactionValues = processedScatterData
                    .map((d) => d.interaction_value)
                    .filter((v): v is number => typeof v === "number");
                  if (interactionValues.length === 0) {
                    return "rgba(70, 130, 180, 0.7)";
                  }
                  const min = Math.min(...interactionValues);
                  const max = Math.max(...interactionValues);
                  const normalized = (value - min) / (max - min || 1);
                  return `rgba(${Math.round(normalized * 255)}, 0, ${Math.round(
                    (1 - normalized) * 255
                  )}, 0.7)`;
                }
              }
            : {
                color: "rgba(70, 130, 180, 0.7)"
              }
        }
      ]
    };

    return option;
  }
};
